import { create } from 'zustand';
import { CreditService, type CreditBalance } from '@/services/CreditService';

type CreditState = {
  balance: CreditBalance | null;
  isLoading: boolean;
  error: string | null;
  lastFetched: number | null;
};

type CreditActions = {
  fetchBalance: () => Promise<void>;
  reset: () => void;
  /** Hard clear — wipes balance on logout so stale data doesn't bleed across accounts */
  clear: () => void;
};

const svc = new CreditService();
const STALE_MS = 30_000; // refetch after 30s

export const useCreditStore = create<CreditState & CreditActions>((set, get) => ({
  balance: null,
  isLoading: false,
  error: null,
  lastFetched: null,

  fetchBalance: async () => {
    const { lastFetched, isLoading } = get();
    if (isLoading) return;
    if (lastFetched && Date.now() - lastFetched < STALE_MS) return;

    set({ isLoading: true, error: null });
    try {
      const balance = await svc.getBalance();
      set({ balance, isLoading: false, lastFetched: Date.now() });
    } catch (err: any) {
      set({ isLoading: false, error: err?.message || 'Failed to fetch balance' });
    }
  },

  reset: () => set({ lastFetched: null }),

  clear: () => set({ balance: null, isLoading: false, error: null, lastFetched: null }),
}));
